import { ref, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { createActor, type AnyStateMachine, type Actor, type SnapshotFrom } from 'xstate';
import type { RouteLocationRaw } from 'vue-router';
import type { FlowParams } from '@/types/flow';
import { useStorage } from '@/composables/useStorage';
import { setGoingBack } from '@/animations/navigation-direction';
import { LINKS } from '@/utils/links';

export interface FlowCacheOption {
  key: string;
}

interface FlowCacheData {
  flowId: string;
  snapshot: unknown;
}

export interface XStateFlowConfig {
  machine: AnyStateMachine;
  stepRouteMap: Record<string, string>;
  exitRoute?: RouteLocationRaw;
  cacheOption?: FlowCacheOption;
}

let activeActor: Actor<AnyStateMachine> | null = null;
let activeConfig: XStateFlowConfig | null = null;
const currentStep: Ref<string> = ref('');
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flowContext: Ref<Record<string, any>> = ref({});

export function getActiveXStateFlow() {
  if (!activeActor || !activeConfig) return null;
  return { actor: activeActor, config: activeConfig };
}

export function isXStateFlowActive(): boolean {
  return activeActor !== null;
}

export function clearXStateFlow(): void {
  if (activeActor) {
    activeActor.stop();
  }
  activeActor = null;
  activeConfig = null;
}

function getStateValue(snapshot: SnapshotFrom<AnyStateMachine>): string {
  const value = snapshot.value;
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export function useXStateFlow<T>() {
  const router = useRouter();
  const storage = useStorage();

  // Hydrate from existing actor if mid-flow
  if (activeActor && activeConfig) {
    const snapshot = activeActor.getSnapshot();
    currentStep.value = getStateValue(snapshot);
    flowContext.value = { ...(snapshot.context as { params: Record<string, unknown> }).params };
  }

  function saveCache(actor: Actor<AnyStateMachine>, config: XStateFlowConfig): void {
    if (!config.cacheOption) return;
    const cacheData: FlowCacheData = {
      flowId: config.machine.id,
      snapshot: actor.getPersistedSnapshot(),
    };
    storage.setItem(config.cacheOption.key, cacheData);
  }

  function clearCache(config: XStateFlowConfig): void {
    if (!config.cacheOption) return;
    storage.removeItem(config.cacheOption.key);
  }

  function subscribe(actor: Actor<AnyStateMachine>, config: XStateFlowConfig) {
    let isInitial = true;
    actor.subscribe((snapshot) => {
      if (activeActor !== actor) return;

      const step = getStateValue(snapshot);
      currentStep.value = step;
      flowContext.value = { ...snapshot.context.params };

      const routePath = config.stepRouteMap[step];
      if (!routePath) return;

      const flowPaths = Object.values(config.stepRouteMap);
      const currentPath = router.currentRoute.value.path;
      const userInsideFlow = flowPaths.some((p) => currentPath === p);
      if (!isInitial && !userInsideFlow) return;

      if (isInitial) {
        isInitial = false;
        router.push({ path: routePath });
      } else {
        router.replace({ path: routePath });
      }

      saveCache(actor, config);
    });
  }

  function start(config: XStateFlowConfig, initialParams?: T): void {
    if (activeActor) {
      activeActor.stop();
    }
    clearCache(config);

    const actor = createActor(config.machine, {
      input: { params: initialParams ? { ...initialParams } : {} },
    });
    activeActor = actor;
    activeConfig = config;

    subscribe(actor, config);
    actor.start();
  }

  async function resume(config: XStateFlowConfig, params?: T): Promise<void> {
    // Deep-link with params: always start fresh
    if (params) {
      start(config, params);
      return;
    }

    // Already active: hydrate refs from existing actor
    if (activeActor && activeConfig) {
      const snapshot = activeActor.getSnapshot();
      currentStep.value = getStateValue(snapshot);
      flowContext.value = { ...(snapshot.context as { params: Record<string, unknown> }).params };
      return;
    }

    // Deep-link without params: try to restore from cache
    if (config.cacheOption) {
      const cached = await storage.getItem<FlowCacheData>(config.cacheOption.key);
      const isCacheMatched = cached?.flowId === config.machine.id;
      if (cached && isCacheMatched) {
        const actor = createActor(config.machine, {
          snapshot: cached.snapshot as SnapshotFrom<AnyStateMachine>,
        });
        activeActor = actor;
        activeConfig = config;

        subscribe(actor, config);
        actor.start();
        return;
      }
    }

    // No cache available: redirect to exit
    setGoingBack(true);
    if (config.exitRoute) {
      router.replace(config.exitRoute);
    } else if ((window.history.state?.position ?? 0) > 0) {
      router.back();
    } else {
      router.replace(LINKS.TABS.DASHBOARD);
    }
  }

  function next(stepParams?: FlowParams): void {
    if (!activeActor || !activeConfig) return;

    if (stepParams) {
      activeActor.send({ type: 'UPDATE_CONTEXT', params: stepParams });
    }

    const snapshot = activeActor.getSnapshot();
    const canNext = snapshot.can({ type: 'NEXT' });

    if (!canNext) {
      exit();
      return;
    }

    activeActor.send({ type: 'NEXT' });
  }

  function back(): void {
    if (!activeActor || !activeConfig) {
      exit();
      return;
    }

    const snapshot = activeActor.getSnapshot();
    const canBack = snapshot.can({ type: 'BACK' });

    if (!canBack) {
      exit();
      return;
    }

    setGoingBack(true);
    activeActor.send({ type: 'BACK' });
  }

  function updateParams(params: Partial<T>): void {
    if (!activeActor) {
      return;
    }
    activeActor.send({ type: 'UPDATE_CONTEXT', params });
  }

  function exit(): void {
    if (activeConfig) {
      clearCache(activeConfig);
    }
    const exitRoute = activeConfig?.exitRoute;
    clearXStateFlow();
    setGoingBack(true);
    const canGoBack = (window.history.state?.position ?? 0) > 0;
    if (canGoBack) {
      router.back();
    } else if (exitRoute) {
      router.replace(exitRoute);
    } else {
      router.replace(LINKS.TABS.DASHBOARD);
    }
  }

  return {
    currentStep,
    params: flowContext as Ref<T>,
    start,
    resume,
    next,
    back,
    updateParams,
    exit,
  };
}
